import {
  BarChart3,
  MessageSquareText,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shadcnComponents/ui/card";
import { Progress } from "@/shadcnComponents/ui/progress";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shadcnComponents/ui/tabs";
import { Badge } from "@/shadcnComponents/ui/badge";
import {
  BarChart,
  Brain,
  Clock,
  DollarSign,
  MessageSquare,
  PieChart,
  Users,
} from "lucide-react";
import { IBrandSentiments } from "../CampaignInterface/LiveCampaignInterface";
import { capitalizeFirstLetter } from "@/Helpers/helper";
import { MdOpenInNew } from "react-icons/md";

const competitorData = {
  // Generic data
  overview: {
    mostMentioned: {
      name: "Swiggy",
      mentions: 45,
      percentage: 45,
    },
    totalMentions: 100,
    keyAdvantage: {
      feature: "Speed",
      description: "Fastest delivery times",
    },
  },
  competitors: [
    {
      name: "Swiggy",
      mentions: 45,
      percentage: 45,
      color: "#FC8019",
      initials: "S",
    },
    {
      name: "Zomato",
      mentions: 35,
      percentage: 35,
      color: "#CB202D",
      initials: "Z",
    },
    {
      name: "Others",
      mentions: 20,
      percentage: 20,
      color: "#A0AEC0",
      initials: "O",
    },
  ],
  comparisonFactors: [
    {
      factor: "Speed of Delivery",
      leader: "Zepto",
      score: 90,
      comment: "Zepto is the fastest!",
    },
    {
      factor: "Food Freshness",
      leader: "Swiggy",
      score: 85,
      comment: "Swiggy has fresher food.",
    },
    {
      factor: "Pricing",
      leader: "Zomato",
      score: 80,
      comment: "Zomato is cheaper.",
    },
  ],
  competitorInsights: [
    {
      name: "Swiggy",
      initials: "S",
      color: "#FC8019",
      keyLearning: "Swiggy is known for its wide range of options.",
      praisePoints: "Wide range of options",
      critiquePoints: "Delivery times can be slow",
      audienceInclination: "Loyal customer base",
      exampleComments: [
        "Swiggy has everything I need!",
        "Swiggy is always reliable.",
      ],
    },
    {
      name: "Zomato",
      initials: "Z",
      color: "#CB202D",
      keyLearning: "Zomato is known for its competitive pricing.",
      praisePoints: "Competitive pricing",
      critiquePoints: "Customer service issues",
      audienceInclination: "Price-sensitive customers",
      exampleComments: ["Zomato is the cheapest!", "Zomato has great deals."],
    },
  ],
  keyTakeaways: [
    "Zepto needs to improve its food freshness.",
    "Swiggy needs to improve its delivery times.",
    "Zomato needs to improve its customer service.",
  ],

  // Aaina's specific data
  aaina: {
    overview: {
      mostMentioned: {
        name: "LIC",
        mentions: 10,
        percentage: 50,
      },
      totalMentions: 20,
      keyAdvantage: {
        feature: "Trust",
        description: "Trusted brand for generations",
      },
    },
    competitors: [
      {
        name: "LIC",
        mentions: 10,
        percentage: 50,
        color: "#1E88E5",
        initials: "L",
      },
      {
        name: "HDFC Life",
        mentions: 5,
        percentage: 25,
        color: "#0077B5",
        initials: "H",
      },
      {
        name: "SBI Life",
        mentions: 5,
        percentage: 25,
        color: "#E53935",
        initials: "S",
      },
    ],
    comparisonFactors: [
      {
        factor: "Trust",
        leader: "LIC",
        score: 95,
        comment: "LIC is the most trusted brand.",
      },
      {
        factor: "Returns",
        leader: "HDFC Life",
        score: 80,
        comment: "HDFC Life offers better returns.",
      },
      {
        factor: "Customer Service",
        leader: "SBI Life",
        score: 75,
        comment: "SBI Life has better customer service.",
      },
    ],
    competitorInsights: [
      {
        name: "LIC",
        initials: "L",
        color: "#1E88E5",
        keyLearning: "LIC is known for its legacy and trust.",
        praisePoints: "Legacy and trust",
        critiquePoints: "Slow to innovate",
        audienceInclination: "Conservative investors",
        exampleComments: [
          "LIC is the most trusted brand!",
          "LIC is always reliable.",
        ],
      },
      {
        name: "HDFC Life",
        initials: "H",
        color: "#0077B5",
        keyLearning: "HDFC Life is known for its innovative products.",
        praisePoints: "Innovative products",
        critiquePoints: "Less trust than LIC",
        audienceInclination: "Modern investors",
        exampleComments: [
          "HDFC Life has great products!",
          "HDFC Life is always innovating.",
        ],
      },
    ],
    keyTakeaways: [
      "LIC needs to innovate to stay relevant.",
      "HDFC Life needs to build more trust.",
      "SBI Life needs to improve its returns.",
    ],
  },

  // Richa's specific data
  richa: {
    overview: {
      mostMentioned: {
        name: "Swiggy",
        mentions: 25,
        percentage: 62.5,
      },
      totalMentions: 40,
      keyAdvantage: {
        feature: "Convenience",
        description: "Easy to use and reliable",
      },
    },
    competitors: [
      {
        name: "Swiggy",
        mentions: 25,
        percentage: 62.5,
        color: "#FC8019",
        initials: "S",
      },
      {
        name: "Zomato",
        mentions: 10,
        percentage: 25,
        color: "#CB202D",
        initials: "Z",
      },
      {
        name: "Others",
        mentions: 5,
        percentage: 12.5,
        color: "#A0AEC0",
        initials: "O",
      },
    ],
    comparisonFactors: [
      {
        factor: "Convenience",
        leader: "Swiggy",
        score: 90,
        comment: "Swiggy is the most convenient.",
      },
      {
        factor: "Pricing",
        leader: "Zomato",
        score: 85,
        comment: "Zomato is cheaper.",
      },
      {
        factor: "Service",
        leader: "Swiggy",
        score: 80,
        comment: "Swiggy has better service.",
      },
    ],
    competitorInsights: [
      {
        name: "Swiggy",
        initials: "S",
        color: "#FC8019",
        keyLearning: "Swiggy is known for its convenience and reliability.",
        praisePoints: "Convenience and reliability",
        critiquePoints: "Can be expensive",
        audienceInclination: "Loyal customers",
        exampleComments: [
          "Swiggy is the most convenient!",
          "Swiggy is always reliable.",
        ],
      },
      {
        name: "Zomato",
        initials: "Z",
        color: "#CB202D",
        keyLearning: "Zomato is known for its competitive pricing.",
        praisePoints: "Competitive pricing",
        critiquePoints: "Customer service issues",
        audienceInclination: "Price-sensitive customers",
        exampleComments: ["Zomato is the cheapest!", "Zomato has great deals."],
      },
    ],
    keyTakeaways: [
      "Swiggy needs to maintain its convenience and reliability.",
      "Zomato needs to improve its customer service.",
    ],
    noCompetitorsMessage: "No competitors were mentioned in the comments.",
    exampleComments: ["No competitors were mentioned in the comments."],
    overallInsight: "No competitors were mentioned in the comments.",
  },
};

export function BrandSentiment({
  brandSentimentData,
}: {
  brandSentimentData: IBrandSentiments;
}) {
  const data = brandSentimentData;
  const totalComments =
    (data?.positive_comments || 0) +
    (data?.negative_comments || 0) +
    (data?.neutral_comments || 0);

  const totalDepthComments =
    (data?.superficial_comments || 0) + (data?.detailed_comments || 0);

  const hasNoNegativeSentiment = data?.negative_comments === 0;

  const getRandomColor = () => {
    const hue = Math.floor(Math.random() * 360);
    return `hsl(${hue}, 70%, 60%)`;
  };

  // Calculate percentages
  const getPercentage = (value: number): string =>
    ((value / totalComments) * 100).toFixed(2);

  const positivePercentage = getPercentage(data?.positive_comments || 0);
  const negativePercentage = getPercentage(data?.negative_comments || 0);
  const neutralPercentage = getPercentage(data?.neutral_comments || 0);

  // Render keywords list
  const renderKeywordsList = (
    keywords: { keyword: string; frequency: number }[],
    color: string
  ) => {
    if (!keywords?.length) return null;
    const maxFrequency = Math.max(...keywords.map((item) => item.frequency));

    return keywords
      .sort((a, b) => b.frequency - a.frequency)
      .map((item, index) => (
        <div key={index} className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`h-3 w-3 rounded-full bg-${color}-500`}></div>
              <span className="font-medium">"{item?.keyword}"</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {item?.frequency} occurrences
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className={`h-full bg-${color}-500`}
              style={{ width: `${(item?.frequency / maxFrequency) * 100}%` }}
            ></div>
          </div>
          {/* <Progress
          value={item?.frequency}
          className="h-2"
          indicatorClassName={`bg-${color}-500`}
        /> */}
        </div>
      ));
  };

  const renderSentimentKeywordList = (
    keywords: { keyword: string; frequency: number }[],
    color: string
  ) => {
    if (!keywords?.length) return null;
    const maxFrequency = Math.max(...keywords.map((item) => item.frequency));

    return keywords.map((keyword, index) => {
      const progressValue = (keyword?.frequency / maxFrequency) * 100 || 0;
      return (
        <div key={index} className="space-y-1">
          <div className="text-sm">"{keyword?.keyword}"</div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className={`h-full ${color}`}
              style={{ width: `${progressValue}%` }}
            ></div>
          </div>
        </div>
      );
    });
  };
  // Render example comments
  const renderExampleComments = (
    examples: { comment: string; link: string }[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    icon: any,
    color: string
  ) => {
    if (!examples?.length) return null;

    return examples.map((comment, index) => (
      <div
        key={index}
        className={`rounded-lg border border-${color}-100 bg-${color}-50 p-4`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`mt-1 h-8 w-8 rounded-full bg-${color}-100 flex items-center justify-center`}
          >
            {icon}
          </div>
          <div>
            <p className="text-sm  gap-1 items-center justify-between line-clamp-3 italic text-slate-700">
              "{comment?.comment}"{" "}
            </p>
            <div className="mt-2 flex items-center gap-2">
              <Badge
                className={`bg-${color}-100 text-${color}-800 hover:bg-${color}-100`}
              >
                {color === "green"
                  ? "Very Positive"
                  : color === "amber"
                  ? index === 0
                    ? "Very Negative"
                    : "Negative"
                  : "Neutral"}
              </Badge>
            </div>
          </div>
          <span>
            <span onClick={() => window.open(comment?.link, "_blank")}>
              <MdOpenInNew className="h-4 w-4 text-slate-500 cursor-pointer" />
            </span>
          </span>
        </div>
      </div>
    ));
  };

  // Render recommended actions
  const renderRecommendedActions = (actions: string[], color: string) => {
    if (!actions?.length) return null;

    return (
      <div className={`mt-6 rounded-lg border p-4 bg-white`}>
        <h4 className={`font-medium text-${color}-800 mb-2`}>
          Recommended Actions
        </h4>
        <ul className="space-y-2 text-sm text-slate-700">
          {actions.map((action, index) => (
            <li key={index} className="flex items-start gap-2">
              <div
                className={`mt-0.5 h-5 w-5 rounded-full bg-${color}-100 flex items-center justify-center text-${color}-600 text-xs`}
              >
                {index + 1}
              </div>
              <p>{action}</p>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-green-600 to-blue-500 bg-clip-text text-transparent">
          Brand Sentiment Analysis
        </h2>
      </div>

      {/* Top summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-700">
              Overall Sentiment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div className="text-3xl font-bold text-green-900">Positive</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-700">
              Positive Comments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <div className="text-3xl font-bold text-green-900">
                  {data?.positive_comments}
                </div>
                <div className="text-sm text-slate-500">
                  {positivePercentage}% of total
                </div>
              </div>
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <ThumbsUp className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-amber-700">
              Negative Comments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <div className="text-3xl font-bold text-amber-900">
                  {data?.negative_comments}
                </div>
                <div className="text-sm text-slate-500">
                  {negativePercentage}% of total
                </div>
              </div>
              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                <ThumbsDown className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-sky-50 border-blue-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-blue-700">
              Neutral Comments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <div className="text-3xl font-bold text-blue-900">
                  {data?.neutral_comments}
                </div>
                <div className="text-sm text-slate-500">
                  {neutralPercentage}% of total
                </div>
              </div>
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <MessageSquareText className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sentiment Overview */}
      <Card className="border-green-100 overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
          <CardTitle className="text-green-800">Sentiment Overview</CardTitle>
          <CardDescription>
            Distribution of positive, negative, and neutral comments
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex justify-center mb-4">
                <div className="relative h-60 w-60">
                  <svg
                    viewBox="0 0 100 100"
                    className="h-full w-full transform -rotate-90"
                  >
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="transparent"
                      stroke="#e2e8f0"
                      stroke-width="20"
                    />

                    {/* Positive sentiment slice */}
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="transparent"
                      stroke="#10B981"
                      strokeWidth="20"
                      strokeDasharray={`${
                        (data?.positive_comments /
                          (data?.positive_comments +
                            data?.negative_comments +
                            data?.neutral_comments)) *
                        251
                      } 251`}
                      strokeDashoffset="0"
                      className="drop-shadow-md"
                    />
                    {/* Neutral sentiment slice */}
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="transparent"
                      stroke="#3B82F6"
                      strokeWidth="20"
                      strokeDasharray={`${
                        (data?.neutral_comments /
                          (data?.positive_comments +
                            data?.negative_comments +
                            data?.neutral_comments)) *
                        251
                      } 251`}
                      strokeDashoffset={`${
                        -(
                          data?.positive_comments /
                          (data?.positive_comments +
                            data?.negative_comments +
                            data?.neutral_comments)
                        ) * 251
                      }`}
                      className="drop-shadow-md"
                    />
                    {/* Negative sentiment slice */}
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="transparent"
                      stroke="#F59E0B"
                      strokeWidth="20"
                      strokeDasharray={`${
                        (data?.negative_comments ||
                          0 /
                            (data?.positive_comments +
                              data?.negative_comments +
                              data?.neutral_comments)) * 251
                      } 251`}
                      strokeDashoffset={`${
                        -(
                          data?.positive_comments /
                            (data?.positive_comments +
                              data?.negative_comments +
                              data?.neutral_comments) +
                          data?.neutral_comments /
                            (data?.positive_comments +
                              data?.negative_comments +
                              data?.neutral_comments)
                        ) * 251
                      }`}
                      className="drop-shadow-md"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-800">
                        {totalComments}
                      </div>
                      <div className="text-xs text-slate-500">
                        Total Comments
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    <span className="text-sm">Positive</span>
                  </div>
                  <span className="text-sm font-medium">
                    {positivePercentage}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                    <span className="text-sm">Negative</span>
                  </div>
                  <span className="text-sm font-medium">
                    {negativePercentage}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                    <span className="text-sm">Neutral</span>
                  </div>
                  <span className="text-sm font-medium">
                    {neutralPercentage}%
                  </span>
                </div>
              </div>
            </div>

            {data?.emotion_distribution && (
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-green-800 mb-2">
                    Emotions in Comments
                  </h4>
                  <div className="space-y-4">
                    {(() => {
                      const maxEmotionValue = Math.max(
                        ...Object.values(data.emotion_distribution)
                      );

                      return Object.entries(data.emotion_distribution)
                        ?.filter((item) => item[1] > 0)
                        ?.sort((a, b) => b[1] - a[1])
                        .map(([key, value], index) => (
                          <div key={index} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {/* <div
                                  className="h-3 w-3 rounded-full"
                                  style={{ backgroundColor: "#FFF" }}
                                ></div> */}

                                <span className="text-sm">
                                  {" "}
                                  {capitalizeFirstLetter(key)}
                                </span>
                              </div>
                              <span className="text-sm font-medium">
                                {value}%
                              </span>
                            </div>
                            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                              <div
                                className="h-full"
                                style={{
                                  width: `${(value / maxEmotionValue) * 100}%`,
                                  backgroundColor: getRandomColor(),
                                }}
                              ></div>
                            </div>
                          </div>
                        ));
                    })()}
                  </div>
                </div>

                {data?.emotion_summary && (
                  <div className="rounded-lg border p-4 bg-green-50">
                    <h4 className="font-medium text-green-800 mb-2">
                      Emotion Capture Summary
                    </h4>
                    <p className="text-sm text-slate-700">
                      {data.emotion_summary}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {data.comment_depth_insight && (
        <Card className="border-purple-100">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-violet-50">
            <CardTitle className="text-purple-800">
              Comment Depth Analysis
            </CardTitle>
            <CardDescription>
              Distribution of superficial vs. detailed comments
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex justify-center mb-4">
                  <div className="relative h-60 w-60">
                    <svg
                      viewBox="0 0 100 100"
                      className="h-full w-full transform -rotate-90"
                    >
                      {/* Superficial comments slice */}
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="transparent"
                        stroke="#8B5CF6"
                        strokeWidth="20"
                        strokeDasharray={`${
                          (data?.superficial_comments /
                            (data?.superficial_comments +
                              data?.detailed_comments)) *
                          251
                        } 251`}
                        strokeDashoffset="0"
                        className="drop-shadow-md"
                      />
                      {/* Detailed comments slice */}
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="transparent"
                        stroke="#EC4899"
                        strokeWidth="20"
                        strokeDasharray={`${
                          (data?.detailed_comments /
                            (data?.superficial_comments +
                              data?.detailed_comments)) *
                          251
                        } 251`}
                        strokeDashoffset={`${
                          -(
                            data?.superficial_comments /
                            (data?.superficial_comments +
                              data?.detailed_comments)
                          ) * 251
                        }`}
                        className="drop-shadow-md"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-800">
                          {totalDepthComments}
                        </div>
                        <div className="text-xs text-slate-500">
                          Total Comments
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-purple-500"></div>
                      <span className="text-sm">Superficial</span>
                    </div>
                    <span className="text-sm font-medium">
                      {(
                        (data?.superficial_comments / totalDepthComments) *
                        100
                      )?.toFixed(2)}{" "}
                      %
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-pink-500"></div>
                      <span className="text-sm">Detailed</span>
                    </div>
                    <span className="text-sm font-medium">
                      {(
                        (data?.detailed_comments / totalDepthComments) *
                        100
                      )?.toFixed(2)}{" "}
                      %
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-purple-800 mb-4">
                  Comment Depth Insights
                </h4>
                <div className="rounded-lg border p-4 bg-purple-50">
                  <p className="text-sm text-slate-700">
                    {data?.comment_depth_insight}
                  </p>
                </div>

                <div className="mt-6 space-y-4">
                  <h4 className="text-sm font-medium text-purple-800">
                    Key Takeaways
                  </h4>
                  <div className="space-y-4">
                    {data?.key_takeaways?.map((takeaway, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="mt-0.5 h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                          {index + 1}
                        </div>
                        <p className="text-sm text-slate-700">{takeaway}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="positive" className="space-y-4">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger
            value="positive"
            className="data-[state=active]:bg-white data-[state=active]:text-green-700"
          >
            <ThumbsUp className="mr-2 h-4 w-4" />
            Positive Sentiment
          </TabsTrigger>
          <TabsTrigger
            value="negative"
            className="data-[state=active]:bg-white data-[state=active]:text-amber-700"
          >
            <ThumbsDown className="mr-2 h-4 w-4" />
            Negative Sentiment
          </TabsTrigger>
          <TabsTrigger
            value="neutral"
            className="data-[state=active]:bg-white data-[state=active]:text-blue-700"
          >
            <MessageSquareText className="mr-2 h-4 w-4" />
            Neutral Sentiment
          </TabsTrigger>
          <TabsTrigger
            value="keywords"
            className="data-[state=active]:bg-white data-[state=active]:text-purple-700"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Keyword Analysis
          </TabsTrigger>
        </TabsList>

        <TabsContent value="positive" className="space-y-4">
          <Card className="border-green-100">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
              <CardTitle className="text-green-800">
                Positive Sentiment Analysis
              </CardTitle>
              <CardDescription>
                {data?.positive_comments} comments with positive sentiment
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  {data?.top_positive_keywords?.length > 0 && (
                    <>
                      <h4 className="text-sm font-medium text-green-800 mb-4">
                        Common Praise Keywords
                      </h4>
                      <div className="space-y-4">
                        {renderKeywordsList(
                          data.top_positive_keywords,
                          "green"
                        )}
                      </div>
                    </>
                  )}

                  {data?.sentiment_insights?.positive && (
                    <div className="mt-6 rounded-lg border p-4 bg-green-50">
                      <h4 className="font-medium text-green-800 mb-2">
                        Positive Sentiment Insight
                      </h4>
                      <p className="text-sm text-slate-700">
                        {data.sentiment_insights.positive}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  {data?.positive_examples?.length > 0 && (
                    <>
                      <h4 className="text-sm font-medium text-green-800 mb-4">
                        Example Positive Comments
                      </h4>
                      <div className="space-y-4">
                        {renderExampleComments(
                          data.positive_examples?.slice(0, 3),
                          <ThumbsUp className="h-4 w-4 text-green-600" />,
                          "green"
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="negative" className="space-y-4">
          {hasNoNegativeSentiment ? (
            <Card className="border-amber-100">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
                <CardTitle className="text-amber-800">
                  Negative Sentiment Analysis
                </CardTitle>
                <CardDescription>
                  No negative comments identified
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <Card className="border-amber-100">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
                <CardTitle className="text-amber-800">
                  Negative Sentiment Analysis
                </CardTitle>
                <CardDescription>
                  {data?.negative_comments} comments with negative sentiment
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    {data?.top_negative_keywords?.length > 0 && (
                      <>
                        <h4 className="text-sm font-medium text-amber-800 mb-4">
                          Common Critique Keywords
                        </h4>
                        <div className="space-y-4">
                          {renderKeywordsList(
                            data.top_negative_keywords,
                            "amber"
                          )}
                        </div>
                      </>
                    )}

                    {data?.sentiment_insights?.negative && (
                      <div className="mt-6 rounded-lg border p-4 bg-amber-50">
                        <h4 className="font-medium text-amber-800 mb-2">
                          Negative Sentiment Insight
                        </h4>
                        <p className="text-sm text-slate-700">
                          {data.sentiment_insights.negative}
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    {data?.negative_examples?.length > 0 && (
                      <>
                        <h4 className="text-sm font-medium text-amber-800 mb-4">
                          Example Negative Comments
                        </h4>
                        <div className="space-y-4">
                          {renderExampleComments(
                            data.negative_examples.slice(0, 3),
                            <ThumbsDown className="h-4 w-4 text-amber-600" />,
                            "amber"
                          )}
                        </div>
                      </>
                    )}

                    {data?.recommended_actions?.negative &&
                      renderRecommendedActions(
                        data.recommended_actions.negative,
                        "amber"
                      )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="neutral" className="space-y-4">
          <Card className="border-blue-100">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-sky-50">
              <CardTitle className="text-blue-800">
                Neutral Sentiment Analysis
              </CardTitle>
              <CardDescription>
                {data?.neutral_comments} comments with neutral sentiment
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  {data?.top_neutral_keywords?.length > 0 && (
                    <>
                      <h4 className="text-sm font-medium text-blue-800 mb-4">
                        Common Neutral Keywords
                      </h4>
                      <div className="space-y-4">
                        {renderKeywordsList(data.top_neutral_keywords, "blue")}
                      </div>
                    </>
                  )}

                  {data?.sentiment_insights?.neutral && (
                    <div className="mt-6 rounded-lg border p-4 bg-blue-50">
                      <h4 className="font-medium text-blue-800 mb-2">
                        Neutral Sentiment Insight
                      </h4>
                      <p className="text-sm text-slate-700">
                        {data.sentiment_insights.neutral}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  {data?.neutral_examples?.length > 0 && (
                    <>
                      <h4 className="text-sm font-medium text-blue-800 mb-4">
                        Example Neutral Comments
                      </h4>
                      <div className="space-y-4">
                        {renderExampleComments(
                          data.neutral_examples.slice(0, 3),
                          <MessageSquareText className="h-4 w-4 text-blue-600" />,
                          "blue"
                        )}
                      </div>
                    </>
                  )}

                  {data?.recommended_actions?.neutral &&
                    renderRecommendedActions(
                      data.recommended_actions.neutral,
                      "blue"
                    )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="keywords" className="space-y-4">
          <Card className="border-purple-100">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-violet-50">
              <CardTitle className="text-purple-800">
                Keyword Analysis
              </CardTitle>
              <CardDescription>
                Comparative analysis of keywords across sentiment categories
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <h4 className="text-lg font-medium text-purple-800 mb-4">
                Top Keywords by Sentiment
              </h4>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* Positive Keywords */}
                {data?.top_positive_keywords?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-3 w-3 rounded-full bg-green-500"></div>
                      <span className="font-medium text-green-700">
                        Positive Keywords
                      </span>
                    </div>
                    <div className="space-y-4">
                      {renderSentimentKeywordList(
                        data.top_positive_keywords,
                        "bg-green-500"
                      )}
                    </div>
                  </div>
                )}

                {/* Negative Keywords */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                    <span className="font-medium text-amber-700">
                      Negative Keywords
                    </span>
                  </div>
                  <div className="space-y-4">
                    {data?.top_negative_keywords?.length > 0 ? (
                      renderSentimentKeywordList(
                        data.top_negative_keywords,
                        "bg-amber-500"
                      )
                    ) : (
                      <div className="text-sm text-slate-500 italic">
                        No negative keywords identified
                      </div>
                    )}
                  </div>
                </div>

                {/* Neutral Keywords */}
                {data?.top_neutral_keywords?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                      <span className="font-medium text-blue-700">
                        Neutral Keywords
                      </span>
                    </div>
                    <div className="space-y-4">
                      {renderSentimentKeywordList(
                        data.top_neutral_keywords,
                        "bg-blue-500"
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <Card className="border-purple-100">
                  <CardHeader>
                    <CardTitle className="text-base font-medium text-purple-800">
                      Sentiment Word Cloud
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="h-64 flex items-center justify-center">
                    <div className="relative w-full h-full flex items-center justify-center">
                      {/* Simulated word cloud with different sized words */}
                      {data?.top_positive_keywords?.[0]?.keyword && (
                        <div
                          className="absolute text-green-500 text-4xl font-bold"
                          style={{ top: "30%", left: "25%" }}
                        >
                          {data.top_positive_keywords[0].keyword}
                        </div>
                      )}
                      {data?.top_neutral_keywords?.[0]?.keyword && (
                        <div
                          className="absolute text-blue-500 text-2xl"
                          style={{ top: "60%", left: "20%" }}
                        >
                          {data.top_neutral_keywords[0].keyword}
                        </div>
                      )}
                      <div
                        className="absolute text-green-500 text-3xl"
                        style={{ bottom: "40%", right: "20%" }}
                      >
                        {data?.top_positive_keywords?.[1]?.keyword}
                      </div>
                      <div
                        className="absolute text-amber-500 text-2xl"
                        style={{ top: "20%", right: "30%" }}
                      >
                        {data?.top_negative_keywords?.[0]?.keyword}
                      </div>
                      <div
                        className="absolute text-blue-500 text-xl"
                        style={{ bottom: "30%", left: "30%" }}
                      >
                        {data?.top_neutral_keywords?.[1]?.keyword}
                      </div>
                      <div
                        className="absolute text-green-500 text-2xl"
                        style={{ top: "50%", right: "25%" }}
                      >
                        {data?.top_positive_keywords?.[2]?.keyword ||
                          "game-changer"}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-purple-100">
                  <CardHeader>
                    <CardTitle className="text-base font-medium text-purple-800">
                      Key Takeaways
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {data?.key_takeaways
                        ?.slice(0, 5)
                        ?.map((takeaway, index) => (
                          <div key={index} className="flex items-start gap-3">
                            <div className="mt-0.5 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                              {index + 1}
                            </div>
                            <p className="text-sm text-slate-700">{takeaway}</p>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Modify the CompetitorsValues component to use the appropriate data
export function CompetitorsValues({ influencer = null }) {
  // Use appropriate data based on the selected influencer
  let data;

  if (influencer === 0) {
    // Use Aaina's data for first influencer
    data = competitorData.aaina;
  } else if (influencer === 1) {
    // Use Richa's data for second influencer
    data = competitorData.richa;
  } else {
    data = competitorData;
  }

  // Check if there are no competitors
  const hasNoCompetitors = data.competitors && data.competitors.length === 0;

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-[#E7F3FF] to-white border-[#C7E6FF]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#0A66C2]">
              Most Mentioned Competitor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <div className="text-2xl font-bold text-[#191919]">
                  {data.overview.mostMentioned.name}
                </div>
                <div className="text-sm text-[#666666]">
                  {data.overview.mostMentioned.mentions} mentions (
                  {data.overview.mostMentioned.percentage}%)
                </div>
              </div>
              {!hasNoCompetitors && (
                <div className="h-10 w-10 rounded-full bg-[#1E88E5] flex items-center justify-center text-white">
                  {data.overview.mostMentioned.name === "Swiggy"
                    ? "S"
                    : data.overview.mostMentioned.name === "Zomato"
                    ? "Z"
                    : data.overview.mostMentioned.name === "LIC"
                    ? "L"
                    : "N"}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#E7F3FF] to-white border-[#C7E6FF]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#0A66C2]">
              Total Competitor Mentions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div className="text-2xl font-bold text-[#191919]">
                {data.overview.totalMentions}
              </div>
              {!hasNoCompetitors && (
                <div className="flex gap-2">
                  {data.competitors.map((comp, index) => (
                    <div
                      key={index}
                      className="h-6 w-6 rounded-full flex items-center justify-center text-white text-xs"
                      style={{ backgroundColor: comp.color }}
                    >
                      {comp.mentions}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#E7F3FF] to-white border-[#C7E6FF]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#0A66C2]">
              Key Competitive Advantage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between">
              <div>
                <div className="text-2xl font-bold text-[#191919]">
                  {data.overview.keyAdvantage.feature}
                </div>
                <div className="text-sm text-[#666666]">
                  {data.overview.keyAdvantage.description}
                </div>
              </div>
              <div className="h-10 w-10 rounded-full bg-[#E7F3FF] flex items-center justify-center">
                <Clock className="h-5 w-5 text-[#0A66C2]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* No Competitors Message */}
      {hasNoCompetitors && (
        <Card className="border-[#C7E6FF]">
          <CardHeader>
            <CardTitle className="text-[#0A66C2]">
              No Competitor Mentions
            </CardTitle>
            <CardDescription>
              Analysis of competitor mentions in comments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-6">
              <div className="h-12 w-12 rounded-full bg-[#E7F3FF] flex items-center justify-center">
                <PieChart className="h-6 w-6 text-[#0A66C2]" />
              </div>
              <p className="text-[#666666]">{data.noCompetitorsMessage}</p>
            </div>

            {/* Example Comments */}
            {data.exampleComments && data.exampleComments.length > 0 && (
              <div className="rounded-lg bg-[#F8FAFF] p-4 mt-4 mb-4">
                <h3 className="text-lg font-semibold text-[#0A66C2] mb-3">
                  Example Comments
                </h3>
                <ul className="space-y-2 text-[#666666]">
                  {data.exampleComments.map((comment, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="mt-1 h-5 w-5 rounded-full bg-[#E7F3FF] flex items-center justify-center text-[#0A66C2]">
                        {index + 1}
                      </div>
                      <p>"{comment}"</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Overall Insight */}
            {data.overallInsight && (
              <div className="rounded-lg bg-[#F8FAFF] p-4 mt-4">
                <h3 className="text-lg font-semibold text-[#0A66C2] mb-3">
                  Overall Insights
                </h3>
                <p className="text-[#666666] mb-4">{data.overallInsight}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Mentions Analysis - Only show if there are competitors */}
      {!hasNoCompetitors && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-[#C7E6FF]">
            <CardHeader>
              <CardTitle className="text-[#0A66C2]">
                Competitor Mentions Distribution
              </CardTitle>
              <CardDescription>
                Breakdown of all competitors mentioned in comments
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {data.competitors.map((competitor, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: competitor.color }}
                      ></div>
                      <span className="font-medium">{competitor.name}</span>
                      {index === 0 && (
                        <Badge className="bg-[#E7F3FF] text-[#0A66C2] hover:bg-[#C7E6FF]">
                          Most Mentioned
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">
                        {competitor.mentions} mentions
                      </span>
                      <span className="text-sm text-[#666666]">
                        ({competitor.percentage}%)
                      </span>
                    </div>
                  </div>
                  <Progress
                    value={competitor.percentage}
                    className="h-2"
                    // indicatorClassName="transition-all"
                    style={{ backgroundColor: "rgba(0,0,0,0.05)" }}
                    color={competitor.color}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-[#C7E6FF]">
            <CardHeader>
              <CardTitle className="text-[#0A66C2]">Share of Voice</CardTitle>
              <CardDescription>
                Proportional representation of competitor mentions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center mb-6">
                <div className="relative h-60 w-60">
                  <PieChart className="h-full w-full text-[#E7F3FF]" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-xl font-bold text-[#0A66C2]">
                        {data.overview.totalMentions}
                      </div>
                      <div className="text-xs text-[#666666]">
                        Total Mentions
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-6 right-6 space-y-3">
                    {data.competitors.map((competitor, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: competitor.color }}
                        ></div>
                        <span className="text-xs">{competitor.name}</span>
                        <span className="text-xs text-[#666666]">
                          {competitor.percentage}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-center text-sm text-[#666666]">
                <span className="text-[#0A66C2] font-medium">
                  {data.overview.mostMentioned.name}
                </span>{" "}
                dominates the conversation with{" "}
                <span className="text-[#0A66C2] font-medium">
                  {data.overview.mostMentioned.percentage}%
                </span>{" "}
                of all competitor mentions in the comments.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Comparison Factors - Only show if there are competitors */}
      {!hasNoCompetitors &&
        data.comparisonFactors &&
        data.comparisonFactors.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-[#191919] flex items-center gap-2">
              <BarChart className="h-6 w-6 text-[#0A66C2]" />
              Comparison Factors
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.comparisonFactors.map((factor, index) => (
                <Card key={index} className="bg-[#F8FAFF]">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-[#0A66C2]">
                        {factor.factor === "Speed of Delivery" ||
                        factor.factor === "Speed" ? (
                          <Clock className="h-5 w-5" />
                        ) : factor.factor === "Food Freshness" ||
                          factor.factor === "Service" ? (
                          <ThumbsUp className="h-5 w-5" />
                        ) : (
                          <DollarSign className="h-5 w-5" />
                        )}
                        <CardTitle>{factor.factor}</CardTitle>
                      </div>
                      <Badge
                        style={{
                          backgroundColor:
                            factor.leader === "Zepto"
                              ? "#7C3AED"
                              : factor.leader === "Swiggy"
                              ? "#FC8019"
                              : factor.leader === "LIC"
                              ? "#1E88E5"
                              : "#CB202D",
                        }}
                      >
                        {factor.leader}
                      </Badge>
                    </div>
                    <CardDescription>
                      Leader: {factor.leader} ({factor.score})
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-[#666666]">
                      <div className="flex items-center gap-1 mb-1">
                        <MessageSquare className="h-3 w-3 text-[#0A66C2]" />
                        <span className="font-medium">
                          Comment from LinkedIn:
                        </span>
                      </div>
                      <p className="italic text-[#191919]">
                        "{factor.comment}"
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

      {/* Competitor Learnings & Audience Inclination - Only show if there are competitors */}
      {!hasNoCompetitors &&
        data.competitorInsights &&
        data.competitorInsights.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-[#191919] flex items-center gap-2">
              <Users className="h-6 w-6 text-[#0A66C2]" />
              Competitor Learnings & Audience Inclination
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.competitorInsights.map((competitor, index) => (
                <Card
                  key={index}
                  className="border-t-4"
                  style={{ borderTopColor: competitor.color }}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-xl">
                        {competitor.name}
                      </CardTitle>
                      <div
                        className="h-8 w-8 rounded-full flex items-center justify-center text-white"
                        style={{ backgroundColor: competitor.color }}
                      >
                        {competitor.initials}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex items-center gap-2 text-[#0A66C2] mb-2">
                        <Brain className="h-4 w-4" />
                        <h4 className="font-medium">Key Learnings</h4>
                      </div>
                      <p className="text-sm">{competitor.keyLearning}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-lg bg-[#E7F3FF] p-3">
                        <div className="flex items-center gap-2 text-[#0A66C2] mb-1">
                          <ThumbsUp className="h-3 w-3" />
                          <h5 className="text-xs font-medium">Praise Points</h5>
                        </div>
                        <p className="text-xs">{competitor.praisePoints}</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <div className="flex items-center gap-2 text-slate-600 mb-1">
                          <ThumbsUp className="h-3 w-3 rotate-180" />
                          <h5 className="text-xs font-medium">
                            Critique Points
                          </h5>
                        </div>
                        <p className="text-xs">{competitor.critiquePoints}</p>
                      </div>
                    </div>

                    <div className="rounded-lg bg-[#E7F3FF] p-3">
                      <div className="flex items-center gap-2 text-[#0A66C2] mb-1">
                        <Users className="h-3 w-3" />
                        <h5 className="text-xs font-medium">
                          Audience Inclination
                        </h5>
                      </div>
                      <p className="text-xs">
                        {competitor.audienceInclination}
                      </p>
                    </div>

                    {competitor.exampleComments &&
                      competitor.exampleComments.length > 0 && (
                        <div className="rounded-lg bg-slate-50 p-3">
                          <div className="flex items-center gap-2 text-slate-600 mb-1">
                            <MessageSquare className="h-3 w-3" />
                            <h5 className="text-xs font-medium">
                              Example Comments
                            </h5>
                          </div>
                          {competitor.exampleComments.map((comment, idx) => (
                            <p key={idx} className="text-xs italic mt-1">
                              "{comment}"
                            </p>
                          ))}
                        </div>
                      )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

      {/* Key Takeaways */}
      <Card className="border-[#C7E6FF]">
        <CardHeader>
          <CardTitle className="text-[#0A66C2]">
            Key Takeaways & Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {data.keyTakeaways.map((takeaway, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="mt-0.5 h-6 w-6 rounded-full bg-[#E7F3FF] flex items-center justify-center text-[#0A66C2]">
                  {index + 1}
                </div>
                <p className="text-[#191919]">{takeaway}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
